import { DialogAccessCredentials, type VERIFYTRANSACTIONSTAGETYPE, verifyTransactionTitle } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	handleApiError,
	HTTPSTATUSCODE,
	type IUseAccessCredentialsRequest,
	OTPLENGTH,
	RECOVERY_CODES_MAX,
	showToast,
	type TwoFaType,
} from 'BreetHelpers';
import { useAccessCredentials, useFormStage, useMfaTransactionsApi, useModalApi, useRecoveryCodeApi } from 'BreetHooks';
import { useGetUserPreferenceQuery, useLazyVerifyPinQuery } from 'BreetRedux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useDialogVerifyTransaction = () => {
	const { fetchOTP, isApiloading, skipPin, transactionType, closeModal, dismissModal } = useMfaTransactionsApi();

	const { data: userPreferenceData, isLoading: userPreferenceLoading } = useGetUserPreferenceQuery(
		undefined,
		alwaysRefetchApiQueryOptions
	);

	const [verifyPinFn] = useLazyVerifyPinQuery();

	const [pin, setPin] = useState<string>('');

	const { recoveryCodeValue, handleRecoveryCodeValue, setRecoveryCodeValue } = useRecoveryCodeApi();

	const { closeModalDirect, handleOpenModal, openModalDirect } = useModalApi();

	const formStage = useFormStage<VERIFYTRANSACTIONSTAGETYPE>(
		skipPin ? 'otp-verification' : 'pin-verification',
		['pin-verification', 'otp-verification', 'recovery-code', 'exhausted-recovery-code'],
		verifyTransactionTitle,
		'verifyTransaction'
	);

	const skipPinAndMfa = useMemo(
		() => transactionType === 'convertFiatToUSD' || transactionType === 'sell-usd',
		[transactionType]
	);

	const isMfa = useMemo(
		() => !!userPreferenceData?.data?.isAuthenticator2FaActive || !!userPreferenceData?.data?.is2FAActive,
		[userPreferenceData]
	);

	const skipMfa = useMemo(() => transactionType === 'swapCurrency' || !isMfa, [isMfa, transactionType]);

	const submitRequest = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				showToast('error', 'Something went wrong');
				return;
			}

			const defaultType = !userPreferenceData?.data?.isAuthenticator2FaActive ? 'email' : 'authenticator';

			const otpProps: TwoFaType = {
				code: data.code,
				type: formStage.isStageActive('recovery-code') ? 'recoveryCode' : defaultType,
			};

			const request = fetchOTP(pin, otpProps);

			if (!request) {
				closeModalDirect('loadingDialog');
				return;
			}

			request
				.then(() => {
					setPin('');
					data.onSuccess?.();
					closeModal();
				})
				.catch((e: unknown) => {
					data.onError(e);
				});
		},
		[fetchOTP, formStage, closeModal, closeModalDirect, pin, userPreferenceData?.data?.isAuthenticator2FaActive]
	);

	const refetchOTP = useCallback(async () => {
		await fetchOTP(pin)?.catch(handleApiError);
	}, [fetchOTP, pin]);

	const description = !userPreferenceData?.data?.isAuthenticator2FaActive
		? `Enter the ${OTPLENGTH}-digit code sent to your <span class='color-tertiary-400'>email inbox</span> to continue`
		: `Enter the ${OTPLENGTH}-digit code from your <span class='color-tertiary-400'>authenticator app</span> to continue`;

	const isExhaustedRecoveryCode = (userPreferenceData?.data?.usedRecoveryCodesCount ?? 0) >= RECOVERY_CODES_MAX;

	const submitRecoveryCode = useCallback(() => {
		if (isExhaustedRecoveryCode) {
			formStage.navigator.goTo('exhausted-recovery-code')();
			return;
		}

		submitRequest({
			code: recoveryCodeValue,
			onError: () => {
				setRecoveryCodeValue('');
			},
		});
	}, [isExhaustedRecoveryCode, submitRequest, recoveryCodeValue, formStage.navigator, setRecoveryCodeValue]);

	const { component: otpComponent } = useAccessCredentials({
		id: '2fa-step',
		mode: 'otp',
		submitRequest,
		dialogMessage: 'Verifying OTP...',
		title: 'Verify Your Identity',
		description,
		type: 'collect',
		persistOnVerify: true,
		generateOTP: false,
		resendOtp: userPreferenceData?.data?.is2FAActive,
		render: (props) => (
			<DialogAccessCredentials
				{...props}
				refetchFn={refetchOTP}
				otpCta={
					!userPreferenceData?.data?.isAuthenticator2FaActive
						? undefined
						: {
								btnTitle: 'Use Recovery Code',
								message: "Can't find code?",
								action: formStage.navigator.goTo(isExhaustedRecoveryCode ? 'exhausted-recovery-code' : 'recovery-code'),
							}
				}
			/>
		),
	});

	const requestEmailOTP = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				showToast('error', 'Something went wrong');
				return;
			}

			const request = fetchOTP(data.code);

			if (!request) {
				closeModalDirect('loadingDialog');
				return;
			}

			request
				.then(() => {
					data.onSuccess?.();
					closeModal();
				})
				.catch((e: unknown) => {
					closeModalDirect('loadingDialog');

					const error = e as { data: { message: string }; status: number };

					if (error.status === HTTPSTATUSCODE.TRANSACTIONNOAUTH && data.code) {
						setPin(data.code);

						formStage.navigator.goTo('otp-verification')();
					} else {
						data.onError(e);
					}
				});
		},
		[closeModal, closeModalDirect, fetchOTP, formStage.navigator]
	);

	const requestAuthenticatorOtp = useCallback(
		(pinCodeArg: string) => {
			setPin(pinCodeArg);

			formStage.navigator.goTo('otp-verification')();
		},
		[formStage.navigator]
	);

	const onVerifyPINSuccess = useCallback(
		(data: IUseAccessCredentialsRequest) => {
			closeModalDirect('loadingDialog');

			if (!data.code) {
				return;
			}

			if (skipMfa) {
				const request = fetchOTP(data.code);

				if (!request) {
					closeModalDirect('loadingDialog');
					return;
				}

				request
					.then(() => {
						data.onSuccess?.();
						if (transactionType === 'saveWalletAddress') {
							closeModalDirect('verifyTransaction');
							return;
						}

						closeModal();
					})
					.catch((e: unknown) => {
						data.onError(e);
					});
				return;
			}

			if (userPreferenceData?.data?.isAuthenticator2FaActive) {
				requestAuthenticatorOtp(data.code);
			} else {
				requestEmailOTP(data);
			}
		},
		[
			closeModalDirect,
			fetchOTP,
			closeModal,
			requestAuthenticatorOtp,
			requestEmailOTP,
			skipMfa,
			userPreferenceData?.data?.isAuthenticator2FaActive,
			transactionType,
		]
	);

	const pinVerification = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!data?.code) {
				return;
			}

			verifyPinFn({ pin: data.code })
				.unwrap()
				.then(() => {
					onVerifyPINSuccess(data);
				})
				.catch((e: unknown) => {
					data.onError(e);
				});
		},
		[onVerifyPINSuccess, verifyPinFn]
	);

	const { component: pinComponent } = useAccessCredentials({
		id: 'withdraw-request',
		mode: 'pin',
		submitRequest: pinVerification,
		dialogMessage: 'Processing request...',
		type: 'collect',
		render: (props) => <DialogAccessCredentials {...props} />,
	});

	const isAutoFetchOtp = useMemo(
		() => skipPinAndMfa || !!(skipPin && !isMfa) || !!(skipPin && !!userPreferenceData?.data?.is2FAActive),
		[skipPinAndMfa, skipPin, isMfa, userPreferenceData]
	);

	const hasAutoFetchedRef = useRef<boolean>(false);

	useEffect(() => {
		if (!isAutoFetchOtp || hasAutoFetchedRef.current) return;

		hasAutoFetchedRef.current = true;

		openModalDirect({ id: 'loadingDialog', metadata: { message: 'Processing request...' } });

		const request = fetchOTP('');

		if (!request) {
			closeModalDirect('loadingDialog');
			return;
		}

		request
			.then(() => {
				closeModal();
			})
			.catch((e: unknown) => {
				closeModalDirect('loadingDialog');
				handleApiError(e);

				if (skipPinAndMfa) dismissModal();
			});
	}, [isAutoFetchOtp, openModalDirect, fetchOTP, closeModal, dismissModal, closeModalDirect, skipPinAndMfa]);

	const closeVerification = () => closeModalDirect('verifyTransaction');

	return {
		formStage,
		skipPin,
		skipPinAndMfa,
		userPreferenceData,
		userPreferenceLoading,
		isApiloading,
		pinComponent,
		otpComponent,
		recoveryCodeValue,
		handleRecoveryCodeValue,
		submitRecoveryCode,
		handleOpenModal,
		closeVerification,
	};
};
